import {F, INTL_ACTION, INTL_ENTITY, getIntlTranslation} from '@/utils/intl';
import React, {useEffect, useRef} from 'react';
import {useDispatch, useIntl, useSelector} from 'umi';
import {Button, Modal, Skeleton, Space, message} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';

import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import Drawer from '@/components/Drawer';
import ServiceRequestDescriptions from '@/components/Kanban/ServiceRequests/ServiceRequestDescriptions';
import ServiceRequestMigrationInfo from '@/components/Lead/ServiceRequestMigrationInfo';
import {ROUTE_PATHS} from '../../../../config/RoutePaths';
import isEmpty from 'lodash/isEmpty';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import {deleteServiceRequestRequest} from '@/services/api/serviceRequest';
import {getAPIErrorMessage, processAPIResponseNow} from '@/utils/request';
import {usePubSub} from '@/context/PubSubContext';
import {APPOINTMENT_CHANGED} from '@/context/PubSubContext/events';

interface ServiceRequestDetailsDrawerState {
  appointmentId?: string;
  open?: boolean;
  action?: string;
  ignoreRouting?: boolean;
  errors?: Record<string, any>;
  toggledAt?: number;
}

const ServiceRequestDetailsDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { publish } = usePubSub();
  const canDelete = useHasAccess([PERMISSIONS.ServiceRequests.DELETE, PERMISSIONS.BUSINESS_PARTNER]);

  const drawer = useSelector(ServiceRequestSelectors.selectServiceRequestDetailsDrawer) as ServiceRequestDetailsDrawerState;
  const appointmentId = drawer?.appointmentId;
  const appointment = useSelector((state: DefaultRootState) => appointmentId ? ServiceRequestSelectors.selectServiceRequestById(state, appointmentId) : null);
  const loading = useSelector(
    (state: DefaultRootState) =>
      state.loading.effects['serviceRequest/fetchSingleAppointment'],
  );
  const open = drawer?.open;
  const previousRoute = useRef<string | null>(null);

  function toggle() {
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
    });
  }

  const fetchData = () => {
    dispatch({
      type: 'serviceRequest/fetchSingleAppointment',
      payload: {
        appointmentId,
      },
    });
  };
  useEffect(() => {
    if (!drawer?.ignoreRouting) {
      if (open) {
        previousRoute.current = window.location.pathname;
        if (!window.location.pathname.includes('/appointments/'))
          window.history.pushState({}, '', `/appointments/${appointmentId}`);
      } else if (previousRoute.current && typeof previousRoute.current === 'string') {
        if (previousRoute.current.includes('/appointments/'))
          return window.history.pushState({}, '', ROUTE_PATHS.CALENDAR);
        window.history.pushState({}, '', previousRoute.current);
      }

    }

  }, [open]);

  useEffect(() => {
    if (appointmentId && drawer?.errors?.[appointmentId] && open)
      toggle();

  }, [appointmentId, drawer?.errors, open]);

  useEffect(() => {
    if (open && appointmentId)
      fetchData();

  }, [appointmentId, open]);

  const getTitle = () => {
    if (!appointment) return (
      <F entity={INTL_ENTITY.APPOINTMENT} action={INTL_ACTION.DETAILS} />
    );

    return (
      <Space>
        <F entity={INTL_ENTITY.APPOINTMENT} action={INTL_ACTION.DETAILS} />
        <small>{appointment.title || appointment.what}</small>
      </Space>
    );
  };


  function handleDelete() {
    Modal.confirm({
      title: getIntlTranslation(intl, { entity: INTL_ENTITY.APPOINTMENT, action: INTL_ACTION.REMOVE }),
      content: getIntlTranslation(intl, { entity: INTL_ENTITY.APPOINTMENT, action: INTL_ACTION.REMOVE_CONFIRMATION }),
      okText: getIntlTranslation(intl, { entity: INTL_ENTITY.APPOINTMENT, action: INTL_ACTION.REMOVE }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await deleteServiceRequestRequest({ params: { serviceRequestId: appointmentId } });
        processAPIResponseNow(result, {
          onSuccess() {
            message.success(getIntlTranslation(intl, { entity: INTL_ENTITY.APPOINTMENT, action: INTL_ACTION.REMOVE_SUCCESS }));
            toggle();
            // The calendar's event list is only ever upserted into (setCalendarEvents),
            // so the refetch below can't drop the deleted chip — remove it explicitly.
            dispatch({ type: 'serviceRequest/removeCalendarEvent', payload: { calendarEventId: appointmentId } });
            dispatch({ type: 'serviceRequest/fetchAppointmentList' });
            // The appointment list above is this drawer's own surface; the calendar
            // and an open lead drawer are refreshed by the event's subscribers.
            publish(APPOINTMENT_CHANGED, { appointmentId, leadId: appointment?.lead?.id });
          },
          onError(data) {
            message.error(
              getAPIErrorMessage(
                data,
                intl.formatMessage({ id: 'common.error.generic', defaultMessage: 'An error occurred. Please try again.' }),
              ),
            );
          },
        });
      },
    });
  }

  function getFooter() {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.8 }}>
        <div>
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete} size="small">
              <F id="common.actions.delete_permanently" defaultMessage="Delete permanently" />
            </Button>
          )}
        </div>
        <Space>
          {appointment?.migration_source && (
            <ServiceRequestMigrationInfo serviceRequest={appointment} />
          )}
        </Space>
      </div>
    );
  }

  return (
    <Drawer
      data-testid="service-request-details-drawer"
      width={800}
      footer={getFooter()}
      title={getTitle()}
      open={open}
      destroyOnHidden
      toggledAt={drawer?.toggledAt}
      onOpenChange={toggle}
    >
      {(isEmpty(appointment) && loading) ? <Skeleton /> :
        <ServiceRequestDescriptions
          loading={loading}
          action={drawer?.action}
          appointment={appointment || {}}
          onClose={toggle}
          onRefresh={fetchData}
        />}
    </Drawer>
  );
};

export default ServiceRequestDetailsDrawer;
